import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarCheck, Sparkles } from 'lucide-react';
import {
  formatCountDelta,
  formatNumber,
  formatRate,
  formatTime,
  messages,
  type DashboardResponse,
} from '@nesc/shared';
import { api, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { usePollingInterval, useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { JobProgress, MetricCard, PeriodBanner } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { toast } from 'sonner';

const PERIOD_OPTIONS = [
  { value: 'today', label: messages.dashboard.periodToday },
  { value: 'week', label: messages.dashboard.periodWeek },
  { value: 'month', label: messages.dashboard.periodMonth },
  { value: 'lastMonth', label: messages.dashboard.periodLastMonth },
] as const;

const resolvePeriod = (value: string): { from: string; to: string } => {
  const now = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  switch (value) {
    case 'today':
      return { from: iso(now), to: iso(now) };
    case 'week': {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() + 1);
      return { from: iso(start), to: iso(now) };
    }
    case 'lastMonth': {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
      return { from: iso(start), to: iso(end) };
    }
    default: {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      return { from: iso(start), to: iso(now) };
    }
  }
};

/**
 * S10 ダッシュボード。
 * スマホでは 要対応 → 実行中 → 今日のアポイント → 指標 → AI 提案 の順で表示する
 * （「今やること」の確認が目的のため）。
 */
export const DashboardPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { filters, setFilter } = useUrlFilters({ period: 'month' });
  const period = resolvePeriod(filters.period as string);

  const { data, isLoading, isFetching, dataUpdatedAt } = useQuery({
    queryKey: qk.dashboard(period),
    queryFn: () => api.get<SingleResponse<DashboardResponse>>('/dashboard', period),
    // 実質リアルタイム：表示中は 10 秒ごとに自動更新し、タブ復帰時も即更新する
    refetchInterval: usePollingInterval(true, 10_000),
    refetchOnWindowFocus: true,
  });

  const cancelJob = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/cancel`),
    onSuccess: () => {
      toast.success('停止を受け付けました', { description: messages.jobs.confirmCancel });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title={messages.dashboard.title} />
        <SkeletonRows rows={6} />
      </>
    );
  }

  const d = data.data;

  const attentionItems = [
    {
      key: 'unknown',
      label: messages.dashboard.unknownSends(d.needsAttention.unknownSends),
      count: d.needsAttention.unknownSends,
      action: messages.dashboard.check,
      to: '/campaigns?tab=unknown',
    },
    {
      key: 'needsAction',
      label: messages.dashboard.needsActionSends(d.needsAttention.needsActionSends),
      count: d.needsAttention.needsActionSends,
      action: messages.dashboard.check,
      to: '/campaigns?tab=needs_action',
    },
    {
      key: 'replies',
      label: messages.dashboard.openReplies(d.needsAttention.openReplies),
      count: d.needsAttention.openReplies,
      action: messages.dashboard.openInbox,
      to: '/inbox?handlingStatus=open',
    },
    {
      key: 'activities',
      label: messages.dashboard.overdueActivities(d.needsAttention.overdueActivities),
      count: d.needsAttention.overdueActivities,
      action: messages.dashboard.open,
      to: '/activities?overdueOnly=true',
    },
    {
      key: 'unlinked',
      label: `紐付け待ちの受信 ${d.needsAttention.unlinkedInbound} 件`,
      count: d.needsAttention.unlinkedInbound,
      action: messages.dashboard.openInbox,
      to: '/inbox?unlinkedOnly=true',
    },
    // 0 件の項目は表示しない
  ].filter((item) => item.count > 0);

  const t = d.totals;
  const overview = (
    <section>
      <h2 className="mb-2 text-note font-semibold text-foreground-muted">全体</h2>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard label="企業総数" value={formatNumber(t.companies)} unit="社" onClick={() => navigate('/companies')} />
        <MetricCard label="送信した企業" value={formatNumber(t.sent)} unit="社" onClick={() => navigate('/companies?status=sent')} />
        <MetricCard label="返信あり" value={formatNumber(t.replied)} unit="社" onClick={() => navigate('/companies?status=replied')} />
        <MetricCard label="アポ確定" value={formatNumber(t.appointments)} unit="社" onClick={() => navigate('/companies?status=appointment')} />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardContent className="pt-4 lg:pt-5">
            <p className="mb-2 text-body font-semibold">セキュリティ診断の内訳</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              {([['high', '高'], ['medium', '中'], ['low', '低'], ['unknown', '判定保留']] as const).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => navigate(`/companies?securityRisk=${k}`)}
                  className="rounded-md border border-border py-2 transition-colors hover:bg-muted"
                >
                  <span className="block tabular text-h2-sm font-semibold">{formatNumber(t.securityRisk[k])}</span>
                  <span className="text-note text-foreground-muted">{label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between gap-3 pt-4 lg:pt-5">
            <div>
              <p className="text-body font-semibold">メルマガ購読者</p>
              <p className="tabular text-h1-sm font-semibold">{formatNumber(t.newsletterConfirmed)} 人</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/settings/newsletter')}>
              管理
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );

  const metrics = (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <MetricCard
        label={messages.analytics.collected}
        value={formatNumber(d.metrics.collected)}
        unit="件"
        delta={formatCountDelta(d.metrics.collectedDelta)}
        onClick={() => navigate(`/companies?createdAfter=${period.from}`)}
      />
      <MetricCard
        label={messages.analytics.sent + '済み'}
        value={formatNumber(d.metrics.sentCompanies)}
        unit="社"
        delta={formatCountDelta(d.metrics.sentDelta)}
        onClick={() => navigate('/companies?status=sent')}
      />
      <MetricCard
        label={messages.analytics.replied}
        value={formatNumber(d.metrics.repliedCompanies)}
        unit="社"
        note={formatRate(d.metrics.replyRate)}
        onClick={() => navigate('/companies?status=replied')}
      />
      <MetricCard
        label={messages.analytics.appointment}
        value={formatNumber(d.metrics.appointmentCompanies)}
        unit="社"
        note={formatRate(d.metrics.appointmentRate)}
        onClick={() => navigate('/companies?status=appointment')}
      />
    </div>
  );

  const attention = attentionItems.length > 0 && (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle aria-hidden className="size-5" />
          {messages.dashboard.needsAttention}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {attentionItems.map((item) => (
            <li key={item.key} className="flex items-center justify-between gap-3 py-2.5">
              <span className="min-w-0 flex-1 text-body font-semibold">{item.label}</span>
              <Button variant="outline" size="sm" onClick={() => navigate(item.to)}>
                {item.action}
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );

  const running = d.running.length > 0 && (
    <Card>
      <CardHeader>
        <CardTitle>{messages.dashboard.running}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {d.running.map((item) => (
          <JobProgress
            key={`${item.kind}-${item.id}`}
            label={item.label}
            done={item.done}
            total={item.total}
            status={item.status}
            href={item.href}
            onCancel={
              item.canCancel && item.kind === 'job' ? () => cancelJob.mutate(item.id) : undefined
            }
          />
        ))}
      </CardContent>
    </Card>
  );

  const appointments = (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarCheck aria-hidden className="size-5" />
          {messages.dashboard.todayAppointments}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {d.todayAppointments.length === 0 ? (
          <p className="text-body text-foreground-muted">本日の予定はありません</p>
        ) : (
          <ul className="divide-y divide-border">
            {d.todayAppointments.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/companies/${item.companyId}`)}
                  className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-muted"
                >
                  <span className="tabular shrink-0 text-body font-semibold">
                    {formatTime(item.scheduledAt)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-body">{item.companyName}</span>
                  <StatusBadge kind="appointment_format" value={item.format} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );

  const suggestions = (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles aria-hidden className="size-5" />
          {messages.suggestion.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {d.suggestions.length === 0 ? (
          <p className="text-body text-foreground-muted">{messages.empty.suggestions.description}</p>
        ) : (
          <ul className="divide-y divide-border">
            {d.suggestions.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => navigate('/analytics?tab=suggestions')}
                  className="flex w-full items-center gap-2 py-2.5 text-left hover:bg-muted"
                >
                  <span className="min-w-0 flex-1 truncate text-body">{item.title}</span>
                  {item.isHypothesis && (
                    <span className="shrink-0 rounded-control border border-dashed border-border-strong px-1.5 text-note text-foreground-muted">
                      {messages.suggestion.hypothesis}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      <PageHeader
        title={messages.dashboard.title}
        actions={
          <Select
            value={filters.period as string}
            onChange={(e) => setFilter('period', e.target.value)}
            aria-label="集計期間"
            className="w-40"
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        }
      />

      <PeriodBanner
        from={d.period.from}
        to={d.period.to}
        observationDays={d.period.observationDays}
        measuringCount={d.period.measuringCount}
        className="mb-2"
      />

      {/* 自動更新中の表示。更新中はドットが脈打つ */}
      <p className="mb-4 flex items-center gap-1.5 text-note text-foreground-muted" aria-live="polite">
        <span
          aria-hidden
          className={`inline-block size-2 shrink-0 rounded-full ${
            isFetching ? 'animate-pulse bg-primary' : 'bg-foreground-subtle'
          }`}
        />
        自動更新中・最終更新 {new Date(dataUpdatedAt).toLocaleTimeString('ja-JP')}
      </p>

      {/* スマホ：要対応を先に出す */}
      <div className="space-y-4 lg:hidden">
        {attention}
        {overview}
        {running}
        {appointments}
        <section>
          <h2 className="mb-2 text-note font-semibold text-foreground-muted">この期間の動き</h2>
          {metrics}
        </section>
        {suggestions}
      </div>

      {/* PC：指標を先に出す */}
      <div className="hidden space-y-4 lg:block">
        {overview}
        <section>
          <h2 className="mb-2 text-note font-semibold text-foreground-muted">この期間の動き</h2>
          {metrics}
        </section>
        {attention}
        {running}
        <div className="grid gap-4 xl:grid-cols-2">
          {appointments}
          {suggestions}
        </div>
      </div>

      {user && !user.name && (
        <p className="mt-4 text-note text-foreground-subtle">
          表示名が未設定です。設定から登録できます。
        </p>
      )}
    </>
  );
};
